/**
 * ============================================================================
 * File Module : OceanCarrierList
 * File Path   : src/components/Admin/CarrierManagement/OceanCarrierList.jsx
 * Purpose     : Ocean tab of Carrier Management — the ocean carriers in the
 *               backend's allowed_shippingline collection (today only
 *               Freightify), with toggles for rate search and booking.
 *               Shows only what's stored; nothing is generated. Every change is
 *               logged server-side to allowed_shippingline_log (one row per
 *               field changed).
 * API         : GET   allowed-shippingline
 *                     -> { success, data: [rows], message }
 *                     Each row: id (allowed_shippingline row id),
 *                     shipping_line_name, shipping_line_code,
 *                     integrated_platform ("freightify" | "direct" | null —
 *                     display only), status, auto_search, auto_book,
 *                     created_at, updated_at.
 *               PATCH allowed-shippingline/:id/flags
 *                     { auto_search?|auto_book? } as 0/1 (the API also takes
 *                     status, but this page doesn't show or change it)
 *                     -> { data: row, changes, message }
 *                     400 bad value, 404 not found, 409 inactive / changed
 *                     concurrently.
 *               status      1 = active, 0 = inactive (search / booking locked)
 *               auto_search 0 = rate search enabled, 1 = disabled
 *               auto_book   0 = bookable,            1 = non-bookable
 * Module      : Admin / Carrier Management
 * ============================================================================
 */

import React, { useEffect, useMemo, useState } from "react";
import { Input, Switch, Table, Tooltip } from "antd";
import { DownloadOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { Search } from "lucide-react";
import { toast } from "react-toastify";
import { httpinstance } from "../../../axios/api";
import { ContentContainer, TableTitle, TableTop } from "../../../StyledComponents/CreateKeyAccountManager";
import { RightSection } from "../../../StyledComponents/Queries";
import useExcel from "../../../utils/generateExcelFile";
import CarrierPagination from "./CarrierPagination";

// Same inverted meaning as the air flags: 0 = enabled, 1 = disabled. A missing
// value counts as enabled.
const isEnabled = (flag) => Number(flag) !== 1;
const flagValue = (enabled) => (enabled ? 0 : 1);
// status is the other way round: 1 = active. Missing counts as active.
const isActive = (status) => Number(status) !== 0;

// integrated_platform labels. Stored values are matched case-insensitively and
// trimmed; an unknown value is shown as stored so a new platform isn't hidden,
// and a missing one shows "-" (nothing is filled in for it).
const PLATFORM_LABELS = {
  freightify: "Freightify",
  direct: "Direct",
};
const NO_PLATFORM = "__none__"; // filter value for rows with no platform set
const platformKey = (value) => {
  const key = String(value ?? "").trim().toLowerCase();
  return key || NO_PLATFORM;
};
const platformLabel = (value) => {
  const key = platformKey(value);
  if (key === NO_PLATFORM) return "-";
  return PLATFORM_LABELS[key] || String(value).trim();
};

// Code column text. Freightify isn't one shipping line — it brings rates for
// all of them — so a Freightify row without its own code reads "All". Any
// other row shows its code, or "-" when it has none.
const codeLabel = (row) => {
  const code = String(row?.shipping_line_code ?? "").trim();
  if (code) return code;
  return platformKey(row?.integrated_platform) === "freightify" ? "All" : "-";
};

const OceanCarrierList = () => {
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  // "<id>-<field>" of switches with a PATCH in flight, so only that switch spins.
  const [savingKeys, setSavingKeys] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchText, setSearchText] = useState("");
  // Selected platform filter keys. Applied to the whole list before paging —
  // antd's own onFilter would only see the current page's rows.
  const [platformFilter, setPlatformFilter] = useState([]);
  const exceSheetlData = useExcel();

  // Search matches the text shown in each column (name, code, platform,
  // Enabled/Disabled, Bookable/Non-Bookable), case-insensitively.
  const filteredLines = useMemo(() => {
    const term = searchText.trim().toLowerCase();
    const byPlatform = platformFilter.length
      ? lines.filter((l) => platformFilter.includes(platformKey(l.integrated_platform)))
      : lines;
    if (!term) return byPlatform;
    return byPlatform.filter((l) =>
      [
        l.shipping_line_name,
        codeLabel(l) === "-" ? "" : codeLabel(l),
        platformKey(l.integrated_platform) === NO_PLATFORM ? "" : platformLabel(l.integrated_platform),
        isEnabled(l.auto_search) ? "Enabled" : "Disabled",
        isEnabled(l.auto_book) ? "Bookable" : "Non-Bookable",
      ].some((v) => String(v || "").toLowerCase().includes(term))
    );
  }, [lines, searchText, platformFilter]);

  /**
   * fetchLines
   * ----------
   * Loads every allowed shipping line, exactly as stored.
   * API: GET allowed-shippingline -> res.data.data (array)
   */
  const fetchLines = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await httpinstance.get("allowed-shippingline");
      const list = Array.isArray(res?.data?.data) ? res.data.data : [];
      const clean = list.filter((item) => item && item.id != null);
      setLines(clean);
      setCurrentPage((p) => Math.min(p, Math.max(1, Math.ceil(clean.length / pageSize))));
    } catch (err) {
      console.error("Failed to load shipping lines:", err);
      const msg = err?.response?.data?.message || "Failed to load shipping lines";
      setLoadError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLines();
  }, []);

  /**
   * downloadExcel
   * -------------
   * Exports every shipping line matching the search (not just the current
   * page) to an .xlsx file.
   */
  const downloadExcel = () => {
    const rows = filteredLines.map((l, index) => ({
      "S.No": index + 1,
      "Shipping Line": l.shipping_line_name || "-",
      Code: codeLabel(l),
      "Integrated Platform": platformLabel(l.integrated_platform),
      "Rate Search": isEnabled(l.auto_search) ? "Enabled" : "Disabled",
      "Booking Status": isEnabled(l.auto_book) ? "Bookable" : "Non-Bookable",
    }));
    exceSheetlData.generateExcelFile(rows, "Ocean Carrier Management", "ocean-carrier-management.xlsx");
    toast.success(`${rows.length} rows of data downloaded in excel`);
  };

  /**
   * handleToggle
   * ------------
   * Saves one field for a shipping line. The switch flips immediately and is
   * reverted if the PATCH fails; on success the row is replaced with the
   * server's copy. A 409 means the row is
   * out of date (deactivated or changed by someone else), so the list reloads.
   *
   * API: PATCH allowed-shippingline/:id/flags { [field]: 0|1 }
   */
  const handleToggle = async (record, field, value) => {
    const rowId = record.id;
    const key = `${rowId}-${field}`;
    if (savingKeys[key]) return; // a save for this switch is already in flight
    const previous = record[field];
    const setRow = (updater) =>
      setLines((prev) => prev.map((l) => (l.id === rowId ? updater(l) : l)));

    setRow((l) => ({ ...l, [field]: value }));
    setSavingKeys((prev) => ({ ...prev, [key]: true }));
    try {
      const res = await httpinstance.patch(`allowed-shippingline/${rowId}/flags`, { [field]: value });
      const row = res?.data?.data;
      if (row && typeof row === "object" && row.id != null) {
        setRow((l) => ({ ...l, ...row }));
      }
      toast.success(res?.data?.message || "Shipping line updated");
    } catch (err) {
      console.error(`Failed to update ${field}:`, err);
      setRow((l) => ({ ...l, [field]: previous }));
      toast.error(err?.response?.data?.message || "Failed to update shipping line");
      if (err?.response?.status === 409 || err?.response?.status === 404) fetchLines();
    } finally {
      setSavingKeys((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  // Search / booking can't be changed on an inactive line (backend returns 409).
  // Status isn't shown or editable on this page, so this only happens when a
  // line was deactivated directly in the database / API.
  const flagSwitch = (record, field, onLabel, offLabel) => {
    const active = isActive(record.status);
    const control = (
      <Switch
        className="carrierToggle"
        checked={isEnabled(record[field])}
        disabled={!active}
        loading={!!savingKeys[`${record.id}-${field}`]}
        checkedChildren={onLabel}
        unCheckedChildren={offLabel}
        onChange={(checked) => handleToggle(record, field, flagValue(checked))}
      />
    );
    return active ? control : <Tooltip title="This shipping line is inactive and can't be changed">{control}</Tooltip>;
  };

  const totalData = filteredLines.length;
  // A reload or a toggle (e.g. Enabled -> Disabled while searching "enabled")
  // can shrink the matches; never show a page past the last one.
  const page = Math.min(currentPage, Math.max(1, Math.ceil(totalData / pageSize)));
  const pageData = filteredLines.slice((page - 1) * pageSize, page * pageSize);

  // Filter options come from whatever platforms are actually in the data
  // (plus "Not set" when some rows have none), so nothing is offered that
  // would match zero rows.
  const platformFilters = [...new Set(lines.map((l) => platformKey(l.integrated_platform)))]
    .sort((a, b) => (a === NO_PLATFORM) - (b === NO_PLATFORM) || a.localeCompare(b))
    .map((key) => ({
      text: key === NO_PLATFORM ? "Not set" : platformLabel(lines.find((l) => platformKey(l.integrated_platform) === key)?.integrated_platform),
      value: key,
    }));

  const columns = [
    {
      title: "S.No",
      key: "slno",
      align: "center",
      render: (text, record, index) => (page - 1) * pageSize + index + 1,
    },
    {
      title: "Shipping Line",
      key: "shipping_line_name",
      dataIndex: "shipping_line_name",
      align: "center",
      render: (name) => <div className="font-medium">{name || "-"}</div>,
    },
    {
      title: "Code",
      key: "shipping_line_code",
      align: "center",
      render: (record) => {
        const label = codeLabel(record);
        return label === "-" ? "-" : <span className="carrierCodeChip">{label}</span>;
      },
    },
    {
      title: "Integrated Platform",
      key: "integrated_platform",
      dataIndex: "integrated_platform",
      align: "center",
      render: platformLabel,
      filters: platformFilters,
      filteredValue: platformFilter.length ? platformFilter : null,
      // Dropdown is portalled to <body>, so it needs its own class to be themed.
      filterDropdownProps: { rootClassName: "carrierFilterDropdown" },
    },
    {
      title: (
        <Tooltip title="Enabled: shipping line's rates show in ocean search results. Disabled: shipping line is skipped on a new ocean search.">
          <span>Rate Search <InfoCircleOutlined /></span>
        </Tooltip>
      ),
      key: "auto_search",
      align: "center",
      render: (record) => flagSwitch(record, "auto_search", "Enabled", "Disabled"),
    },
    {
      title: (
        <Tooltip title="Bookable: users can book this shipping line from ocean rate results. Non-Bookable: users can see its rates but can't book it.">
          <span>Booking Status <InfoCircleOutlined /></span>
        </Tooltip>
      ),
      key: "auto_book",
      align: "center",
      render: (record) => flagSwitch(record, "auto_book", "Bookable", "Non-Bookable"),
    },
  ];


  return (
    <ContentContainer>
      <TableTop>
        <TableTitle>Shipping Line List</TableTitle>
        <RightSection className="carrierToolbar">
          <Input
            allowClear
            prefix={<Search size={15} color="#a8a29e" />}
            placeholder="Search by shipping line, code or status"
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setCurrentPage(1);
            }}
            className="carrierSearch"
          />
          <button
            disabled={totalData === 0}
            onClick={downloadExcel}
            type="button"
            className="text-white bg-primary hover:bg-secondary font-medium rounded-lg text-sm px-4 py-2 my-2 text-center flex items-center justify-center gap-2 shadow-searchbox disabled:opacity-60"
          >
            <DownloadOutlined />
            <span>Download</span>
          </button>
        </RightSection>
      </TableTop>
      <Table
        className="bookingHistoryTable carrierManagementTable"
        rowKey="id"
        columns={columns}
        dataSource={pageData}
        loading={loading}
        locale={{
          emptyText: loadError ? (
            <div className="py-6 text-red-700">
              {loadError}{" "}
              <button type="button" className="underline text-black" onClick={fetchLines}>
                Retry
              </button>
            </div>
          ) : searchText.trim() && lines.length > 0 ? (
            <div className="py-6">No shipping lines match "{searchText.trim()}"</div>
          ) : platformFilter.length && lines.length > 0 ? (
            <div className="py-6">No shipping lines for the selected platform</div>
          ) : undefined,
        }}
        scroll={{ x: 1000 }}
        pagination={false}
        onChange={(_, filters) => {
          setPlatformFilter(filters?.integrated_platform || []);
          setCurrentPage(1);
        }}
      />
      <CarrierPagination
        total={totalData}
        currentPage={page}
        pageSize={pageSize}
        noun="shipping lines"
        onPageChange={setCurrentPage}
        onPageSizeChange={(n) => {
          setPageSize(n);
          setCurrentPage(1);
        }}
      />
    </ContentContainer>
  );
};

export default OceanCarrierList;
